import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'

import { RemoveIcon, WarningTriangleIcon } from 'images'
import { SUPPORT_ARTICLE_URLS } from 'constants'

import InfoBox from 'components/Shared/InfoBox'

import plus from '../../../../../images/icons/plus-icon.svg'

const languageOptions = [
  { text: 'common.language.en', value: 'en' },
  { text: 'common.language.de', value: 'de' },
  { text: 'common.language.fr', value: 'fr' },
  { text: 'common.language.pt', value: 'pt' },
  { text: 'common.language.cs', value: 'cs' },
  { text: 'common.language.hr', value: 'hr' },
  { text: 'common.language.fi', value: 'fi' },
  { text: 'common.language.sv', value: 'sv' },
  { text: 'common.language.da', value: 'da' },
  { text: 'common.language.no', value: 'no' },
  { text: 'common.language.es', value: 'es' },
  { text: 'common.language.it', value: 'it' },
  { text: 'common.language.nl', value: 'nl' },
  { text: 'common.language.pl', value: 'pl' },
]

const LanguageTabs = ({
  activeLanguage = 'en',
  chosenLanguages,
  onLanguageChange = () => {},
  mode = 'edit', // 'edit' or 'running'
  onLanguageAdd = () => {},
  onLanguageRemove = () => {},
  invalidLocales = [],
  leadFormId,
}) => {
  const { t, i18n } = useTranslation()

  const languageVariationsSupportArticleUrl = SUPPORT_ARTICLE_URLS?.languageVariations?.[i18n.language] || SUPPORT_ARTICLE_URLS?.languageVariations?.en

  const availableLanguageOptions = languageOptions.filter(
    option => !chosenLanguages?.some(lang => lang.value === option.value)
  )

  const isAddLanguageButtonDisabled = leadFormId

  const isLanguageActive = (lang) => activeLanguage === lang.value

  const getTooltipText = () => {
    if (leadFormId) return t('Tips.Recommendations.CreativeComposer.addLanguageDisabledLeadForm')
    return ''
  }

  return (
    <div className="language-tabs">
      {chosenLanguages?.map((lang, index) => {
        const hasNextLanguageActive = activeLanguage === chosenLanguages[index + 1]?.value
        const hasPreviousLanguage = index > 0

        return(
          <div
            key={lang.value}
            className={`language-tab 
            ${hasNextLanguageActive ? 'has-next-language' : ''} 
            ${isLanguageActive(lang) ? 'active' : ''} 
            ${hasPreviousLanguage ? 'has-previous-language' : ''}`}
            onClick={() => onLanguageChange(lang.value)}
          >
            <span className={`language-tab__text ${isLanguageActive(lang) ? 'language-tab__active' : ''}`}>{t(lang.text)}</span>

            {invalidLocales.includes(lang.value) && (
              <span className="language-tab__warning">
                <WarningTriangleIcon width={12} height={12} />
              </span>
            )}

            {chosenLanguages?.length > 1 && mode === 'edit' && (

              <button
                type="button"
                className={`event-control-panel_filter_dropdown_item_cancel remove-language ${isLanguageActive(lang) ? 'language-tab__active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onLanguageRemove(lang.value)
                }}
              >
                <RemoveIcon width={14} height={14} />

              </button>
             
            )}
          </div>
        )})}
      
      {availableLanguageOptions.length > 0 && chosenLanguages?.length < 5 && mode === 'edit' && (
        <div className="add-language-btn-info-box">
          <InfoBox
            body={getTooltipText()}
            withAnchor={false}
            enabled={isAddLanguageButtonDisabled}
          >
            <button
              type="button"
              onClick={() => {
                if (!isAddLanguageButtonDisabled) {
                  onLanguageAdd(availableLanguageOptions[0])
                }
              }}
              className={`add-language-btn ${isAddLanguageButtonDisabled ? 'add-language-btn_disabled' : ''}`}
              disabled={isAddLanguageButtonDisabled}
            >
              <img src={plus} alt="add-language" width={14} height={14} />
            </button>
          </InfoBox>
        </div>
      )}

      {mode === 'edit' && (
        <InfoBox
          title={t('Tips.Recommendations.CreativeComposer.preview.title')}
          body={
            <div className="">
              <p className="mb-2">{t('Tips.Recommendations.CreativeComposer.preview.body1')}</p>

              <Trans
                i18nKey="Tips.Recommendations.CreativeComposer.preview.body2"
                components={{
                  a: (
                    <a
                      href={languageVariationsSupportArticleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline', display: 'inline-block' }}
                    >
                      support article
                    </a>
                  ),
                }}
              />
            </div>
          }
        >
          <p className="d-inline-block infobox_preview_text">{'(' + t('common.preview') + ')'}</p>
        </InfoBox>  
      )}
    </div>
  )
}

LanguageTabs.propTypes = {
  activeLanguage: PropTypes.string.isRequired,
  chosenLanguages: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  mode: PropTypes.string,
  onLanguageAdd: PropTypes.func.isRequired,
  onLanguageRemove: PropTypes.func.isRequired,
  invalidLocales: PropTypes.arrayOf(PropTypes.string),
  isRunning: PropTypes.bool,
  leadFormId: PropTypes.string,
}

export default LanguageTabs
